import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Radio,
  Space,
  Typography,
  Alert,
  Divider,
  Tag,
  Row,
  Col,
  Statistic,
  message,
  Modal,
  List,
  Checkbox
} from 'antd';
import {
  SendOutlined,
  UserOutlined,
  TeamOutlined,
  ToolOutlined,
  GlobalOutlined,
  BellOutlined,
  SearchOutlined,
  PhoneOutlined,
  MailOutlined,
  CheckSquareOutlined
} from '@ant-design/icons';
import api from '../services/api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface BroadcastStats {
  total_users: number;
  total_customers: number;
  total_technicians: number;
}

interface User {
  id: number;
  email: string;
  phone?: string;
  first_name: string;
  last_name: string;
  company_name?: string;
  role: string;
  status?: string;
}

const BroadcastNotificationPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [stats, setStats] = useState<BroadcastStats | null>(null);
  const [recipientType, setRecipientType] = useState<string>('all');
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'customer' | 'technician'>('all');
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);

  useEffect(() => {
    loadStats();
    loadUsers();
  }, []);

  const loadStats = async () => {
    try {
      const response = await api.get('/notifications/broadcast/stats');
      setStats(response.data.data);
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
  };

  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      const response = await api.get('/notifications/broadcast/users');
      setUsers(response.data.data || []);
    } catch (error) {
      console.error('Erreur chargement utilisateurs:', error);
      message.error('Impossible de charger la liste des utilisateurs');
    } finally {
      setUsersLoading(false);
    }
  };

  // Filtrage réactif des utilisateurs (recherche texte + filtre de rôle)
  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      // 1. Filtre rôle
      if (roleFilter === 'customer' && user.role !== 'customer') return false;
      if (roleFilter === 'technician' && user.role !== 'technician') return false;

      // 2. Filtre recherche texte (nom, prénom, téléphone, email, entreprise)
      if (!searchTerm.trim()) return true;
      const term = searchTerm.toLowerCase().trim();
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
      const email = (user.email || '').toLowerCase();
      const phone = (user.phone || '').toLowerCase();
      const company = (user.company_name || '').toLowerCase();
      return (
        fullName.includes(term) ||
        email.includes(term) ||
        phone.includes(term) ||
        company.includes(term)
      );
    });
  }, [users, roleFilter, searchTerm]);

  // Actions de sélection rapide
  const handleSelectAllFiltered = () => {
    const visibleIds = filteredUsers.map((u) => u.id);
    const newSelected = Array.from(new Set([...selectedUsers, ...visibleIds]));
    setSelectedUsers(newSelected);
  };

  const handleDeselectAll = () => {
    setSelectedUsers([]);
  };

  const handlePreview = () => {
    form.validateFields().then((values) => {
      setPreviewData({
        ...values,
        recipient_count: getRecipientCount(),
        recipient_type_label: getRecipientTypeLabel()
      });
      setShowPreview(true);
    }).catch(() => {
      message.error('Veuillez remplir tous les champs obligatoires');
    });
  };

  const handleSend = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const payload = {
        recipients: {
          type: recipientType,
          ...(recipientType === 'specific' && { userIds: selectedUsers })
        },
        notification: {
          type: values.notification_type || 'general',
          title: values.title,
          message: values.message,
          priority: values.priority || 'medium',
          actionUrl: values.action_url || null
        }
      };

      const response = await api.post('/notifications/broadcast/send', payload);

      message.success(response.data.message);
      form.resetFields();
      setSelectedUsers([]);
      setShowPreview(false);
      loadStats(); // Recharger les stats

    } catch (error: any) {
      console.error('Erreur envoi:', error);
      message.error(error.response?.data?.message || 'Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  const getRecipientCount = (): number => {
    if (!stats) return 0;
    switch (recipientType) {
      case 'all':
        return stats.total_users;
      case 'customers':
        return stats.total_customers;
      case 'technicians':
        return stats.total_technicians;
      case 'specific':
        return selectedUsers.length;
      default:
        return 0;
    }
  };

  const getRecipientTypeLabel = (): string => {
    switch (recipientType) {
      case 'all':
        return 'Tous les utilisateurs';
      case 'customers':
        return 'Tous les clients';
      case 'technicians':
        return 'Tous les techniciens';
      case 'specific':
        return 'Utilisateurs sélectionnés';
      default:
        return '';
    }
  };

  const getNotificationIcon = (type: string) => {
    const icons: Record<string, any> = {
      all: <GlobalOutlined />,
      customers: <UserOutlined />,
      technicians: <ToolOutlined />,
      specific: <TeamOutlined />
    };
    return icons[type] || <BellOutlined />;
  };

  const getPriorityColor = (priority: string): string => {
    const colors: Record<string, string> = {
      low: 'default',
      medium: 'blue',
      high: 'orange',
      urgent: 'red'
    };
    return colors[priority] || 'default';
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <SendOutlined /> Envoyer une Notification
      </Title>
      <Paragraph>
        Envoyez des notifications personnalisées à vos clients et techniciens (promotions, conseils, informations, etc.)
      </Paragraph>

      {/* Statistiques */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={8}>
            <Card>
              <Statistic
                title="Total Utilisateurs"
                value={stats.total_users}
                prefix={<TeamOutlined />}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Clients"
                value={stats.total_customers}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Techniciens"
                value={stats.total_technicians}
                prefix={<ToolOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Card>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            priority: 'medium',
            notification_type: 'general'
          }}
        >
          {/* Type de destinataires */}
          <Form.Item
            label={
              <Space>
                <TeamOutlined />
                <Text strong>Destinataires</Text>
              </Space>
            }
            required
          >
            <Radio.Group
              value={recipientType}
              onChange={(e) => setRecipientType(e.target.value)}
              buttonStyle="solid"
            >
              <Radio.Button value="all">
                <Space>
                  {getNotificationIcon('all')}
                  Tous ({stats?.total_users || 0})
                </Space>
              </Radio.Button>
              <Radio.Button value="customers">
                <Space>
                  {getNotificationIcon('customers')}
                  Clients ({stats?.total_customers || 0})
                </Space>
              </Radio.Button>
              <Radio.Button value="technicians">
                <Space>
                  {getNotificationIcon('technicians')}
                  Techniciens ({stats?.total_technicians || 0})
                </Space>
              </Radio.Button>
              <Radio.Button value="specific">
                <Space>
                  {getNotificationIcon('specific')}
                  Sélection ({selectedUsers.length})
                </Space>
              </Radio.Button>
            </Radio.Group>
          </Form.Item>

          {/* Sélection utilisateurs spécifiques avec recherche et filtres */}
          {recipientType === 'specific' && (
            <Form.Item
              label={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <Space>
                    <UserOutlined />
                    <Text strong>Sélectionner les destinataires</Text>
                    <Tag color="blue">{selectedUsers.length} sélectionné(s)</Tag>
                  </Space>
                </div>
              }
              required
            >
              {/* Barre de recherche et actions rapides */}
              <Card
                size="small"
                style={{ marginBottom: 12, backgroundColor: '#fafafa', borderRadius: 8 }}
              >
                <Row gutter={[12, 12]} align="middle">
                  <Col xs={24} md={12}>
                    <Input
                      placeholder="Rechercher par nom, prénom, téléphone, email..."
                      prefix={<SearchOutlined style={{ color: '#8c8c8c' }} />}
                      allowClear
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </Col>
                  <Col xs={24} md={6}>
                    <Radio.Group
                      size="middle"
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e.target.value)}
                      buttonStyle="solid"
                      style={{ width: '100%' }}
                    >
                      <Radio.Button value="all" style={{ width: '33.33%', textAlign: 'center' }}>Tous</Radio.Button>
                      <Radio.Button value="customer" style={{ width: '33.33%', textAlign: 'center' }}>Clients</Radio.Button>
                      <Radio.Button value="technician" style={{ width: '33.33%', textAlign: 'center' }}>Techs</Radio.Button>
                    </Radio.Group>
                  </Col>
                  <Col xs={24} md={6} style={{ textAlign: 'right' }}>
                    <Space>
                      <Button
                        size="small"
                        icon={<CheckSquareOutlined />}
                        onClick={handleSelectAllFiltered}
                        disabled={filteredUsers.length === 0}
                      >
                        Tout cocher ({filteredUsers.length})
                      </Button>
                      <Button
                        size="small"
                        onClick={handleDeselectAll}
                        disabled={selectedUsers.length === 0}
                      >
                        Vider
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Card>

              {/* Liste d'utilisateurs avec Scroll */}
              <Card
                style={{ maxHeight: 360, overflowY: 'auto', borderRadius: 8 }}
                bodyStyle={{ padding: '4px 12px' }}
                loading={usersLoading}
              >
                <Checkbox.Group
                  value={selectedUsers}
                  onChange={(values) => setSelectedUsers(values as number[])}
                  style={{ width: '100%' }}
                >
                  <List
                    dataSource={filteredUsers}
                    locale={{
                      emptyText: searchTerm
                        ? `Aucun utilisateur ne correspond à "${searchTerm}"`
                        : 'Aucun utilisateur disponible'
                    }}
                    renderItem={(user) => (
                      <List.Item
                        style={{
                          padding: '10px 8px',
                          borderBottom: '1px solid #f0f0f0',
                          borderRadius: 4
                        }}
                      >
                        <Checkbox value={user.id} style={{ width: '100%' }}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              width: '100%',
                              flexWrap: 'wrap',
                              gap: 8,
                              marginLeft: 6
                            }}
                          >
                            <Space wrap size="small">
                              {user.role === 'customer' ? (
                                <UserOutlined style={{ color: '#1890ff', fontSize: 16 }} />
                              ) : (
                                <ToolOutlined style={{ color: '#722ed1', fontSize: 16 }} />
                              )}
                              <Text strong style={{ fontSize: 14 }}>
                                {user.first_name} {user.last_name}
                              </Text>
                              {user.company_name && (
                                <Tag color="cyan">{user.company_name}</Tag>
                              )}
                              <Tag color={user.role === 'customer' ? 'blue' : 'purple'}>
                                {user.role === 'customer' ? 'Client' : 'Technicien'}
                              </Tag>
                            </Space>
                            <Space size="middle" wrap>
                              {user.phone && (
                                <Text type="secondary" style={{ fontSize: 13 }}>
                                  <PhoneOutlined style={{ marginRight: 4, color: '#52c41a' }} />
                                  {user.phone}
                                </Text>
                              )}
                              {user.email && (
                                <Text type="secondary" style={{ fontSize: 13 }}>
                                  <MailOutlined style={{ marginRight: 4, color: '#1890ff' }} />
                                  {user.email}
                                </Text>
                              )}
                            </Space>
                          </div>
                        </Checkbox>
                      </List.Item>
                    )}
                  />
                </Checkbox.Group>
              </Card>

              <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {filteredUsers.length} utilisateur(s) affiché(s) sur {users.length} au total
                </Text>
                {selectedUsers.length === 0 && (
                  <Tag color="warning">⚠️ Veuillez sélectionner au moins un destinataire</Tag>
                )}
              </div>
            </Form.Item>
          )}

          <Divider />

          {/* Type de notification */}
          <Form.Item
            label="Type de notification"
            name="notification_type"
          >
            <Select>
              <Option value="general">📢 Information générale</Option>
              <Option value="promotion">🎁 Promotion / Offre</Option>
              <Option value="maintenance_tip">💡 Conseil d'entretien</Option>
              <Option value="maintenance_reminder">⏰ Rappel d'entretien</Option>
              <Option value="announcement">📣 Annonce importante</Option>
              <Option value="alert">⚠️ Alerte</Option>
            </Select>
          </Form.Item>

          {/* Titre */}
          <Form.Item
            label="Titre"
            name="title"
            rules={[
              { required: true, message: 'Le titre est obligatoire' },
              { max: 100, message: 'Maximum 100 caractères' }
            ]}
          >
            <Input
              placeholder="Ex: 🎉 Nouvelle offre d'entretien -20%"
              maxLength={100}
              showCount
            />
          </Form.Item>

          {/* Message */}
          <Form.Item
            label="Message"
            name="message"
            rules={[
              { required: true, message: 'Le message est obligatoire' },
              { max: 500, message: 'Maximum 500 caractères' }
            ]}
          >
            <TextArea
              rows={6}
              placeholder="Rédigez votre message ici..."
              maxLength={500}
              showCount
            />
          </Form.Item>

          {/* Priorité */}
          <Form.Item label="Priorité" name="priority">
            <Radio.Group>
              <Radio.Button value="low">
                <Tag color={getPriorityColor('low')}>Faible</Tag>
              </Radio.Button>
              <Radio.Button value="medium">
                <Tag color={getPriorityColor('medium')}>Moyenne</Tag>
              </Radio.Button>
              <Radio.Button value="high">
                <Tag color={getPriorityColor('high')}>Élevée</Tag>
              </Radio.Button>
              <Radio.Button value="urgent">
                <Tag color={getPriorityColor('urgent')}>Urgente</Tag>
              </Radio.Button>
            </Radio.Group>
          </Form.Item>

          {/* URL d'action (optionnel) */}
          <Form.Item
            label="Lien d'action (optionnel)"
            name="action_url"
            tooltip="URL vers laquelle rediriger l'utilisateur quand il clique sur la notification"
          >
            <Input placeholder="/maintenance-offers" />
          </Form.Item>

          {/* Boutons */}
          <Form.Item>
            <Space>
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handlePreview}
                disabled={recipientType === 'specific' && selectedUsers.length === 0}
              >
                Prévisualiser
              </Button>
              <Button onClick={() => form.resetFields()}>
                Réinitialiser
              </Button>
            </Space>
          </Form.Item>
        </Form>

        {/* Info destinataires */}
        <Alert
          message={`${getRecipientCount()} destinataire(s) sélectionné(s)`}
          type="info"
          showIcon
          icon={<BellOutlined />}
        />
      </Card>

      {/* Modal de prévisualisation */}
      <Modal
        title="Prévisualisation de la notification"
        open={showPreview}
        onOk={handleSend}
        onCancel={() => setShowPreview(false)}
        okText="Envoyer maintenant"
        cancelText="Annuler"
        confirmLoading={loading}
        width={600}
        okButtonProps={{
          icon: <SendOutlined />,
          disabled: recipientType === 'specific' && selectedUsers.length === 0
        }}
      >
        {previewData && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Alert
              message={`Sera envoyé à ${previewData.recipient_count} utilisateur(s)`}
              description={previewData.recipient_type_label}
              type="warning"
              showIcon
            />

            <Card
              style={{
                backgroundColor: '#f6ffed',
                border: '1px solid #b7eb8f'
              }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Tag color={getPriorityColor(previewData.priority)}>
                    {previewData.priority.toUpperCase()}
                  </Tag>
                  <Tag>{previewData.notification_type}</Tag>
                </div>
                <Title level={4} style={{ margin: 0 }}>
                  {previewData.title}
                </Title>
                <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                  {previewData.message}
                </Paragraph>
                {previewData.action_url && (
                  <Text type="secondary">
                    🔗 Action: {previewData.action_url}
                  </Text>
                )}
              </Space>
            </Card>

            <Alert
              message="Confirmation requise"
              description="Cette action enverra la notification à tous les destinataires sélectionnés. Cette action est irréversible."
              type="warning"
              showIcon
            />
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default BroadcastNotificationPage;
